import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useSlackIntegration } from '@/api/partners/integrations/slack/use-slack-integration';
import { useHasOrganization } from '@/core';
import { showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

import { SlackConnectModal } from './slack-connect-modal';
import { SlackIntegrationCard } from './slack-integration-card';
import { SlackSettingsModal } from './slack-settings-modal';

export default function EonOneIntegrations() {
  const { styles } = useStyles(stylesheet);
  const { organization } = useHasOrganization();
  const { slack, error, reason } = useLocalSearchParams<{
    slack?: string;
    error?: string;
    reason?: string;
  }>();
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [connectModalVisible, setConnectModalVisible] = useState(false);

  const {
    data: slackIntegration,
    isPending: isSlackPending,
    refetch: refetchSlackIntegration,
  } = useSlackIntegration({
    enabled: !!organization,
  });

  useEffect(() => {
    if (!slack && !error && !reason) {
      return;
    }

    const handleOAuthReturn = async () => {
      if (slack === 'connected') {
        await refetchSlackIntegration();
        setSettingsModalVisible(true);
      } else if (slack === 'error' || error || reason) {
        let message = 'Could not connect to Slack. Please try again.';

        if (error === 'access_denied') {
          message = 'Slack access was denied.';
        } else if (typeof reason === 'string' && reason.length > 0) {
          message = reason;
        }

        showErrorMessage(message);
        router.replace('/partner/eon-one/integrations');
      }
    };

    handleOAuthReturn();
  }, [slack, error, reason, refetchSlackIntegration]);

  const handleSettingsClose = () => {
    setSettingsModalVisible(false);

    if (slack || error || reason) {
      router.replace('/partner/eon-one/integrations');
    }
  };

  const isLoadingContent = isSlackPending && !slackIntegration;

  return (
    <>
      {isLoadingContent ? (
        <View style={styles.loadingContainer}>
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      ) : (
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
        >
          <Text type="title1">Integrations</Text>

          <View style={styles.section}>
            <Text type="title2">Add Integrations</Text>

            <SlackIntegrationCard
              integration={slackIntegration}
              onConnectPress={() => setConnectModalVisible(true)}
              onManagePress={() => setSettingsModalVisible(true)}
            />
          </View>
        </ScrollView>
      )}

      <SlackConnectModal
        visible={connectModalVisible}
        onClose={() => setConnectModalVisible(false)}
      />

      <SlackSettingsModal
        visible={settingsModalVisible}
        integration={slackIntegration}
        onClose={handleSettingsClose}
      />
    </>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  contentContainer: {
    padding: 56,
    gap: 32,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background_1,
  },
  section: {
    gap: 16,
  },
}));
